/**
 * IndexedDB → canonical backend sync.
 *
 * Pushes every locally-cached project that the backend doesn't already have
 * (or that we haven't yet marked as synced) to POST /api/video-projects/save.
 *
 * Run once per app boot, after auth resolves. Idempotent: safe to call repeatedly.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * Why /save (per-project) instead of /bulk-save (batched)?
 * ─────────────────────────────────────────────────────────────────────────
 * The bulk-save endpoint is a pure-insert utility on the backend — it does
 * not honour `project_data.projectId` for upsert matching. That means every
 * bulk-sync created duplicate rows on the backend, which then fed back into
 * the dashboard as duplicate projects on the next boot.
 *
 * The per-project /save endpoint DOES upsert on `project_id` (the top-level
 * payload field), so routing each sync through /save produces stable
 * projects across reboots and prevents duplicate storms.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * Abandoned-draft auto-cleanup
 * ─────────────────────────────────────────────────────────────────────────
 * Before syncing, we delete local-only projects that are clearly abandoned
 * drafts from "New Project" clicks that were never developed:
 *   - no script
 *   - no characters
 *   - no scenes
 *   - no timeline clips
 *   - not modified within the last N days (DRAFT_TTL_MS)
 * These are typically created when a user clicks "New Project", sees the
 * blank wizard, and closes the tab before saving any content. Without
 * cleanup they accumulate in IDB and re-appear in the dashboard forever.
 */

import { saveProjectToBackend, getProjectsFromBackend } from './backendService';
import { getAllLocalProjects, markProjectSynced, deleteProjectFromIndexedDB } from './dbService';

// 7 days — safe window. Users who genuinely started a project and left it
// alone for a week almost always abandoned it. Content-bearing projects
// are never touched regardless of age.
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** Return true iff this project is an empty draft older than DRAFT_TTL_MS. */
const isAbandonedDraft = (p) => {
    if (!p) return false;
    const hasScript = typeof p.script === 'string' && p.script.trim().length > 0;
    const hasCharacters = Array.isArray(p.characters) && p.characters.length > 0;
    const hasScenes = Array.isArray(p.scenes) && p.scenes.length > 0;
    const hasTimelineClips = Array.isArray(p.timeline?.clips) && p.timeline.clips.length > 0;
    if (hasScript || hasCharacters || hasScenes || hasTimelineClips) return false;

    const lastModified = p.lastModified || p.createdAt || 0;
    if (!lastModified) return true;  // no timestamp on a blank project → treat as old
    return (Date.now() - lastModified) > DRAFT_TTL_MS;
};

export async function syncLocalProjectsToBackend() {
    // 0) Sweep abandoned empty drafts from IDB before we do anything else.
    //    These would otherwise become /save calls that create junk rows.
    const localProjects = await getAllLocalProjects();
    let cleaned = 0;
    for (const p of localProjects) {
        if (isAbandonedDraft(p)) {
            const key = p.id || p.projectId;
            if (!key) continue;
            try {
                await deleteProjectFromIndexedDB(key);
                cleaned += 1;
                console.log(`🧹 Removed abandoned empty draft ${key} (lastModified=${new Date(p.lastModified || 0).toISOString()})`);
            } catch (e) {
                console.warn('Cleanup failed for', key, e?.message);
            }
        }
    }

    // 1) What does the backend already know? (List endpoint omits project_data — cheap.)
    const remoteRes = await getProjectsFromBackend();
    if (!remoteRes.success) {
        return { synced: 0, created: 0, updated: 0, failed: 0, cleaned, tombstoned: 0, skipped: true, reason: remoteRes.error };
    }
    const remoteIds = new Set(remoteRes.projects.map(p => p.id));

    // 1a) Tombstone reconciliation. Any IDB row that we previously synced
    //     (syncedToBackend=true) but the backend no longer has was deleted
    //     on the dashboard. Prune it locally so autosave doesn't immediately
    //     POST it again and take a 410. We intentionally DO NOT prune
    //     un-synced local drafts (the user may still be drafting offline).
    let tombstoned = 0;
    const postCleanup = await getAllLocalProjects();
    for (const p of postCleanup) {
        if (p?.syncedToBackend && p?.projectId && !remoteIds.has(p.projectId)) {
            try {
                await deleteProjectFromIndexedDB(p.id);
                tombstoned += 1;
                console.log(`🪦 Pruned tombstoned project ${p.projectId} (localId=${p.id}) — deleted from dashboard`);
            } catch (e) {
                console.warn('Tombstone prune failed for', p.id, e?.message);
            }
        }
    }

    // 2) Re-read IDB (we just deleted some rows in steps 0 and 1a) and
    //    filter to projects worth pushing to the backend.
    const fresh = await getAllLocalProjects();
    const toSync = fresh.filter(p => {
        if (!p) return false;
        const id = p.projectId || p.id;
        if (!id) return false;
        if (p.syncedToBackend && remoteIds.has(p.projectId)) return false;  // already in sync
        if (p.projectId && remoteIds.has(p.projectId)) return false;        // already on backend
        return true;
    });
    if (toSync.length === 0) {
        return { synced: 0, created: 0, updated: 0, failed: 0, cleaned, tombstoned };
    }

    console.log(`☁️  Syncing ${toSync.length} local-only project(s) to canonical backend via /save …`);

    // 3) Per-project /save (NOT /bulk-save — see header for rationale).
    //    Sequential to avoid stampeding the backend; syncs are rare and
    //    typically small (a handful of drafts per user).
    let created = 0, updated = 0, failed = 0;
    for (const project of toSync) {
        try {
            const res = await saveProjectToBackend(project);
            if (res?.gone) {
                // 410 mid-sync: the project was tombstoned between our
                // listing and this save. Prune from IDB and move on.
                try { await deleteProjectFromIndexedDB(project.id); } catch { /* noop */ }
                tombstoned += 1;
                console.log(`🪦 Mid-sync tombstone: ${project.projectId} (localId=${project.id}) pruned`);
                continue;
            }
            if (res?.success && res?.project_id) {
                // We can't tell from the /save response whether it created
                // or updated the row, so we bucket by whether projectId
                // was known beforehand.
                if (project.projectId) updated += 1; else created += 1;
                // CRITICAL: persist the backend-assigned project_id back
                // to the IDB row. Without this the next boot sees the
                // same row with projectId=null, fails the remoteIds
                // match, and triggers another /save → another duplicate.
                await markProjectSynced(project.id, res.project_id);
            } else {
                failed += 1;
                console.warn(`  ✗ Sync failed for ${project.id}: ${res?.error || 'unknown error'}`);
            }
        } catch (e) {
            failed += 1;
            console.warn(`  ✗ Sync threw for ${project.id}: ${e?.message}`);
        }
    }

    const synced = created + updated;
    console.log(`☁️  IDB sync complete: ${synced} synced (${created} created, ${updated} updated), ${failed} failed, ${cleaned} abandoned, ${tombstoned} tombstoned`);
    return { synced, created, updated, failed, cleaned, tombstoned };
}
